import { Icon, Modal, Select } from '@deity-io/portal-ui';
import { EntityNameDescriptionFields } from 'components/FormFields';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  GetPackageVersionContentInfoDocument,
  useCreateFlowMutation,
  useUpdateFlowMutation
} from 'services/graphql/generated/hooks';
import type { GetFlowQuery } from 'services/graphql/generated/operations';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateEditFlowModalProps {
  isOpen?: boolean;
  isEdit?: boolean;
  versionUuid: string;
  editFlow?: GetFlowQuery;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
}

export interface CreateEditFlowInput {
  name: string;
  description: string;
  // type: Flow['type'];
}

export const CreateFlowInputSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Too short').max(100, 'Too long').required('This is a required field.'),
  description: Yup.string().min(2, 'Too short').max(255, 'Too long')
});

export const CreateEditFlowModal: FC<CreateEditFlowModalProps> = ({
  isOpen = false,
  setOpenState,
  onPackageCreated,
  isEdit,
  editFlow,
  versionUuid
}) => {
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  };
  const [create, { data: createData, loading: createLoading }] = useCreateFlowMutation(refetchQueries);
  const [update, { data: updateData, loading: updateLoading }] = useUpdateFlowMutation(refetchQueries);

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
  }, [createData, updateData]);

  const createOrUpdate = (input: CreateEditFlowInput & { type: string }) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updateFlow(input) : createFlow(input);
  };

  const createFlow = (input: CreateEditFlowInput & { type: string }) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        name: input.name,
        description: input.description,
        schema: {
          _meta: {
            type: input.type
          }
        }
      }
    }).then();
  };

  const updateFlow = (input: CreateEditFlowInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editFlow?.getFlow.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const formik = useFormik({
    initialValues: {
      name: editFlow?.getFlow.name ?? '',
      description: editFlow?.getFlow.description ?? '',
      type: editFlow?.getFlow?.schema['_meta']?.type ?? 'flow'
    },
    validateOnBlur: true,
    validationSchema: CreateFlowInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editFlow?.getFlow) {
      const { name, description, schema } = editFlow?.getFlow || {};
      formik.setValues({
        name: name!,
        description: description!,
        type: schema['_meta']?.type ?? 'flow'
      });
    }
  }, [editFlow?.getFlow]);

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} Flow`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} Flow`;
  };

  if (isEdit && !editFlow) {
    return null!;
  }
  const typeOptions = [
    { value: 'flow', label: 'Publishable flow', description: 'A main flow' },
    { value: 'exceptionFlow', label: 'Exception flow', description: 'Flow to handle exceptions' },
    { value: 'partialFlow', label: 'Partial flow', description: 'Flow that can be used inside other flows' },
    { value: 'gqlQuery', label: 'Gql Query', description: 'Flow for a Gql Query. Publishable to NestJs GQL server' },
    {
      value: 'gqlMutation',
      label: 'Gql Mutation',
      description: 'Flow for a Gql Mutation. Publishable to NestJs GQL server'
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label={`${isEdit ? 'Edit' : 'New'} Flow`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <EntityNameDescriptionFields
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          entity="Flow"
        />
        <Select
          label="type"
          onChange={(val: any) => {
            formik.setFieldValue('type', val?.value);
          }}
          value={typeOptions.find(option => option.value === formik.values.type) || null}
          autoComplete={false}
          options={typeOptions}
          disabled={isEdit}
        />
      </form>
    </Modal>
  );
};
